/**
 * @packageDocumentation
 * Provides the mock server for Superhero Incognito, generating round outcomes, handling wild planning, and exposing RTP configuration hooks.
 */
// server.ts

import { betArea } from "./betArea";

/**
 * Available bet sizes that players can choose from
 * Array of numbers representing betting amounts in currency units
 */
export const betSizes:number[] = [0.5,1,1.5,2,2.5,3,4,5,10,15,20,25,30,40,50];

/**
 * Player's current balance
 * Starting balance set to 10000 currency units
 */
export let Balance = 10000;

/**
 * Symbol used to display currency
 * Set to "$" for USD
 */
export const currencySymbol = "$";

/**
 * Flag to determine if cents should be displayed
 * When true, amounts will show decimal places
 */
export const ShowCent = true;

export type RoundTuple = [number, number, number, number, boolean, ScenarioResult[]];


const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));
const ri = (a: number, b: number) => a + Math.floor(Math.random() * (b - a + 1));
const shuffle = <T,>(arr: T[]) => { const a = arr.slice(); for (let i=a.length-1;i>0;i--){const j=Math.floor(Math.random()*(i+1)); [a[i],a[j]]=[a[j],a[i]];} return a; };
const pickN = <T,>(pool: T[], n: number) => shuffle(pool).slice(0, n);
const rand = <T,>(a: T[]) => a[Math.floor(Math.random() * a.length)];
// Deep partial for nested config objects
type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends object ? DeepPartial<T[K]> : T[K];
};
// const WILD_IDS = [41, 42] as const;
export const WILD_CONFIG = {
  enableInWins: true,
  perSlotChance: 0.5,  // 35% chance per slot to become wild
  maxPerWin: 2,         // cap wilds in a win at 2
};

/* -------------------------------
   ID scheme & helpers (unchanged)
---------------------------------*/

const civilianRow = (border: 1|2|3|4) => { const start = (border - 1) * 5 + 1; return [start, start+1, start+2, start+3, start+4]; };
const superRow    = (border: 1|2|3|4) => { const start = 20 + (border - 1) * 5 + 1; return [start, start+1, start+2, start+3, start+4]; };

export const WILD_CIVIL_ID = 41;   // any CIVILIAN form wild
export const WILD_SUPER_ID = 42;   // any SUPER form wild
const isWildSuper = (id: number) => id === WILD_SUPER_ID;
const isWildCivil = (id: number) => id === WILD_CIVIL_ID;
const isWild      = (id: number) => isWildSuper(id) || isWildCivil(id);
const isSuper     = (id: number) => id >= 21 && id <= 40;
const colorOf = (id: number) => {
  if (isWild(id)) return -1;
  if (id >= 1 && id <= 20) return (id - 1) % 5;
  if (id >= 21 && id <= 40) return (id - 21) % 5;
  return -1;
};
const borderOf = (id: number): 0|1|2|3|4 => {
  if (isWild(id)) return 0;
  if (id >= 1 && id <= 20) return (Math.floor((id - 1) / 5) + 1) as 1|2|3|4;
  if (id >= 21 && id <= 40) return (Math.floor((id - 21) / 5) + 1) as 1|2|3|4;
  return 0;
};
const isNonZeroBorder = (b: 0|1|2|3|4): b is 1|2|3|4 => b !== 0;

const civId = (border: 1|2|3|4, color: number) => civilianRow(border)[color];
const supId = (border: 1|2|3|4, color: number) => superRow(border)[color];

type CardKind = "civilian" | "super";

type ConcreteCard = {
  border: 1 | 2 | 3 | 4;
  color: number;
  kind: CardKind;
};

type WildPreset = {
  border: 1 | 2 | 3 | 4;
  color: number;
};

function describeConcreteCard(id: number): ConcreteCard | null {
  if (isWild(id)) return null;
  const border = borderOf(id);
  const color = colorOf(id);
  if (!isNonZeroBorder(border) || color < 0) return null;
  return {
    border,
    color,
    kind: isSuper(id) ? "super" : "civilian",
  };
}

const DEFAULT_WILD_PRESETS: Record<CardKind, WildPreset[]> = {
  super: [
    { border: 4, color: 3 },
    { border: 3, color: 3 },
    { border: 2, color: 3 },
    { border: 1, color: 2 },
  ],
  civilian: [
    { border: 3, color: 3 },
    { border: 2, color: 3 },
    { border: 4, color: 1 },
    { border: 1, color: 0 },
  ],
};


const COLOR_LABELS: Record<number, { label: string; message: string }> = {
  0: { label: "Teal character", message: "Unlocked! Eliza Guard AKA Terror Titania!" },
  1: { label: "White character", message: "Unlocked! Regina Rocker AKA Wailing Winter!" },
  2: { label: "Pink character", message: "Unlocked! Indie Mindy AKA Pixel Pixie!" },
  3: { label: "Orange character", message: "Unlocked! Scrappy Scott AKA Over Obliterator!" },
  4: { label: "Rainbow character", message: "Unlocked! Mark Lacken AKA The Chroma Kraken!" },
};
const BORDER_LABELS: Record<1|2|3|4, { label: string; message: string }> = {
  1: { label: "Bronze (Black) border", message: "Matching Set! Bronze Pack!" },
  2: { label: "Silver border", message: "Awesome! Silver Pack!" },
  3: { label: "Gold border", message: "Rare! Gold Pack!" },
  4: { label: "Holographic border", message: "Ultra Rare! Holographic Pack!" },
};
const SUPER_WIN_LABEL = { label: "Three Villains", message: "Villains Unite! Secret Mission Unlocked!" } as const;


/* ------------------------------------------
   RTP knobs: set probabilities & win amounts
   - weights sum can be <= 1.0 (remainder = no-win)
   - payouts are ABSOLUTE amounts (currency units)
     (If you later want bet multipliers, we can switch.)
-------------------------------------------*/
type WinScenario = "sameBorder" | "sameCharacter" | "allSuper";
export type ScenarioName = WinScenario | "none";

export type ScenarioResult = {
  scenario: WinScenario;
  amount: number;
  displayText: string;
  detail: string;
  border?: 1 | 2 | 3 | 4;
  color?: number;
};

export const RTP_PROFILES: Record<string, DeepPartial<RTPConfig>> = {
  balanced: {},
  dry: {
    weights: { none: 0.82, sameBorder: 0.10, sameCharacter: 0.06, allSuper: 0.02 },
    wilds: {
      inWins: { perSlotChance: { sameBorder: 0.08, sameCharacter: 0.06, allSuper: 0.04 }, maxPerWin: 1 },
      inNonWins: { perSlotChance: 0.02, maxPerSpin: 1 }
    },
    transform: { allowUpgradeFromNone: false } // OK now
  },
  bonusHeavy: {
    weights: { none: 0.55, sameBorder: 0.22, sameCharacter: 0.18, allSuper: 0.05 },
    wilds: {
      inWins: { perSlotChance: { sameBorder: 0.2, sameCharacter: 0.25, allSuper: 0.15 }, maxPerWin: 2 },
      inNonWins: { perSlotChance: 0.06, maxPerSpin: 2 }
    }
  }
};

// allow patching with deep partials
export function configureRTP(patch: DeepPartial<RTPConfig>) {
  deepMerge(RTP_CONFIG, patch);
}
export function setRTPProfile(name: keyof typeof RTP_PROFILES) {
  const p = RTP_PROFILES[name];
  if (p) deepMerge(RTP_CONFIG, p as DeepPartial<RTPConfig>);
}

export function setWinProbabilities(
  weights: Partial<Record<ScenarioName, number>>,
  options: { normalize?: boolean } = {}
) {
  const weightsPatch = { ...weights } as RTPConfig['weights'];
  const shouldNormalize = options.normalize ?? true;
  if (shouldNormalize) {
    (weightsPatch as RTPConfig['weights']).normalize = true;
  }
  configureRTP({ weights: weightsPatch });
}

export function getRTPConfigSnapshot(): RTPConfig {
  return JSON.parse(JSON.stringify(RTP_CONFIG)) as RTPConfig;
}


export type ManualTransformPlan = {
  type?: "civilian" | "super";
  border?: 1 | 2 | 3 | 4;
  color?: number;
};

export type ManualSymbolPlan = {
  type?: "civilian" | "super" | "wildCivilian" | "wildSuper";
  border?: 1 | 2 | 3 | 4;
  color?: number;
  transformTo?: ManualTransformPlan;
};

export type ManualRoundOverride = {
  cards: [ManualSymbolPlan, ManualSymbolPlan, ManualSymbolPlan];
  transformStrategy?: RTPConfig["transform"]["strategy"];
  persist?: boolean;
};

const DEFAULT_CARD_BORDERS: (1 | 2 | 3 | 4)[] = [3, 2, 4];
const DEFAULT_CARD_COLORS: number[] = [3, 1, 4];

function normalizeBorderValue(value: number | undefined, slot: number): 1 | 2 | 3 | 4 {
  if (typeof value === "number" && value >= 1 && value <= 4) {
    return Math.round(value) as 1 | 2 | 3 | 4;
  }
  return DEFAULT_CARD_BORDERS[slot] ?? 3;
}

function normalizeColorValue(value: number | undefined, slot: number): number {
  if (typeof value === "number" && value >= 0 && value <= 4) {
    return Math.round(value);
  }
  const fallback = DEFAULT_CARD_COLORS[slot];
  if (typeof fallback === "number") {
    return fallback;
  }
  return slot % 5;
}

function symbolPlanToId(plan: ManualSymbolPlan, slot: number): number {
  const type = plan.type ?? "civilian";
  if (type === "wildCivilian") return WILD_CIVIL_ID;
  if (type === "wildSuper") return WILD_SUPER_ID;
  const border = normalizeBorderValue(plan.border, slot);
  const color = normalizeColorValue(plan.color, slot);
  return type === "super" ? supId(border, color) : civId(border, color);
}

function transformPlanToId(plan: ManualSymbolPlan, slot: number, revealId: number): number {
  const transform = plan.transformTo;
  if (!transform) return 0;
  let targetType = transform.type;
  if (!targetType) {
    if (isWildSuper(revealId) || isSuper(revealId)) {
      targetType = "super";
    } else {
      targetType = "civilian";
    }
  }
  const border = normalizeBorderValue(transform.border ?? plan.border, slot);
  const color = normalizeColorValue(transform.color ?? plan.color, slot);
  return targetType === "super" ? supId(border, color) : civId(border, color);
}
export interface RTPConfig {
  // outcome distribution (set none or let it be remainder)
  weights: Partial<Record<ScenarioName, number>> & { normalize?: boolean };
  // payouts are multipliers of current bet
  payouts: Record<WinScenario, number>;
  // wild frequencies & caps
  wilds: {
    inWins: {
      perSlotChance: number | Partial<Record<ScenarioName, number>>; // can set per scenario
      maxPerWin: number;
      enable?: boolean; // default true
    },
    inNonWins: {
      perSlotChance: number;
      maxPerSpin: number;
      enable?: boolean; // default true
    }
  };
  // transformation planning
  transform: {
    strategy: "bestPayout" | "preferSameBorder" | "preferSameCharacter";
    allowUpgradeFromNone: boolean;    // if false, ‘none’ base won’t be upgraded by wilds
  };
  // safety/QA guards
  guards: {
    avoidAccidentalWinsInNone: boolean; // keep non-win branch from accidentally matching
    maxConsecutiveWins?: number;        // hard cap streaks (optional)
  };
}

export const RTP_CONFIG: RTPConfig = {
  weights: {
    // If 'none' omitted, it will become remainder (>=0).
    sameBorder: 0.12,
    sameCharacter: 0.09,
    allSuper: 0.04,
    normalize: true, // normalize weights so they sum to 1
  },
  payouts: { sameBorder: 2, sameCharacter: 5, allSuper: 10 },
  wilds: {
    inWins:    { perSlotChance: { sameBorder: 0.12, sameCharacter: 0.12, allSuper: 0.08 }, maxPerWin: 1, enable: true },
    inNonWins: { perSlotChance: 0.03, maxPerSpin: 1, enable: true },
  },
  transform: { strategy: "bestPayout", allowUpgradeFromNone: false },
  guards: { avoidAccidentalWinsInNone: true, maxConsecutiveWins: undefined }
};



// Minimal deep merge (objects/arrays/primitives)
function deepMerge<T>(target: T, patch: any): T {
  if (!patch || typeof patch !== "object") return target;
  for (const k of Object.keys(patch)) {
    const v = (patch as any)[k];
    const t = (target as any)[k];
    if (v && typeof v === "object" && !Array.isArray(v)) {
      (target as any)[k] = deepMerge(t ?? {}, v);
    } else {
      (target as any)[k] = v;
    }
  }
  return target;
}

// --- NEW: per-round transform hints for the client second flip ---
export let lastTransformTargets: [number, number, number] = [0,0,0]; // 0 = no transform needed

const SCENARIO_PRIORITY: WinScenario[] = ["sameBorder", "sameCharacter", "allSuper"];

type MatchAnalysis = {
  sameBorder: boolean;
  sameCharacter: boolean;
  allSuper: boolean;
  matches: WinScenario[];
  border?: 1 | 2 | 3 | 4;
  color?: number;
};

function analyzeMatches(ids: [number, number, number]): MatchAnalysis {
  const borders = ids.map((id) => borderOf(id));
  const colors = ids.map((id) => colorOf(id));

  const sameBorder = borders[0] !== 0 && borders.every((b) => b === borders[0]);
  const sameCharacter = colors[0] !== -1 && colors.every((c) => c === colors[0]);
  const allSuper = ids.every((id) => isSuper(id));

  const matches: WinScenario[] = [];
  if (sameBorder) matches.push("sameBorder");
  if (sameCharacter) matches.push("sameCharacter");
  if (allSuper) matches.push("allSuper");

  return {
    sameBorder,
    sameCharacter,
    allSuper,
    matches,
    border: sameBorder ? (borders[0] as 1 | 2 | 3 | 4) : undefined,
    color: sameCharacter ? colors[0] : undefined,
  };
}

function payoutForScenario(scenario: WinScenario): number {
  return RTP_CONFIG.payouts[scenario] ?? 0;
}

function totalPayoutForMatches(matches: WinScenario[]): number {
  return matches.reduce((sum, scenario) => sum + payoutForScenario(scenario), 0);
}

// Evaluate the scenario a concrete trio satisfies (no wilds considered equal)
function evaluateScenario(ids: [number,number,number]): WinScenario | "none" {
  if (ids.some(isWild)) return "none";

  const analysis = analyzeMatches(ids);
  let best: WinScenario | "none" = "none";
  let bestPay = -1;

  for (const scenario of analysis.matches) {
    const pay = payoutForScenario(scenario);
    if (pay > bestPay) {
      best = scenario;
      bestPay = pay;
    }
  }

  return best;
}

function computeScenarioResults(ids: [number, number, number], bet: number): ScenarioResult[] {
  if (ids.some(isWild)) return [];

  const analysis = analyzeMatches(ids);
  const results: ScenarioResult[] = [];

  if (analysis.sameBorder && analysis.border) {
    const amount = payoutForScenario("sameBorder") * bet;
    if (amount > 0) {
      const info = BORDER_LABELS[analysis.border] ?? { label: "Border win", message: "Matching Set!" };
      results.push({
        scenario: "sameBorder",
        amount,
        displayText: `${info.message}`,
        detail: info.label,
        border: analysis.border,
      });
    }
  }

  if (analysis.sameCharacter && analysis.color != null && analysis.color >= 0) {
    const amount = payoutForScenario("sameCharacter") * bet;
    if (amount > 0) {
      const info = COLOR_LABELS[analysis.color] ?? { label: "Character win", message: "Unlocked!" };
      results.push({
        scenario: "sameCharacter",
        amount,
        displayText: `${info.message}`,
        detail: info.label,
        color: analysis.color,
      });
    }
  }

  if (analysis.allSuper) {
    const amount = payoutForScenario("allSuper") * bet;
    if (amount > 0) {
      results.push({
        scenario: "allSuper",
        amount,
        displayText: `${SUPER_WIN_LABEL.message}`,
        detail: SUPER_WIN_LABEL.label,
      });
    }
  }

  return results;
}



// Weights → pick scenario (with optional normalization)
function pickScenario(): ScenarioName {
  const w = RTP_CONFIG.weights;
  let sb = w.sameBorder ?? 0, sc = w.sameCharacter ?? 0, as = w.allSuper ?? 0;
  let none = (w as any).none as number | undefined;
  if (none == null) none = Math.max(0, 1 - (sb+sc+as));
  let total = sb + sc + as + none;
  if (w.normalize && total > 0) { sb/=total; sc/=total; as/=total; none/=total; }

  const r = Math.random();
  if (r < as) return "allSuper";
  if (r < as + sb) return "sameBorder";
  if (r < as + sb + sc) return "sameCharacter";
  return "none";
}

// Per-scenario wild chance
function wildRateFor(s: ScenarioName, inWin: boolean): number {
  if (inWin) {
    const cfg = RTP_CONFIG.wilds.inWins;
    const r = typeof cfg.perSlotChance === "number" ? cfg.perSlotChance : (cfg.perSlotChance[s] ?? 0);
    return (cfg.enable === false) ? 0 : r;
  } else {
    const cfg = RTP_CONFIG.wilds.inNonWins;
    return (cfg.enable === false) ? 0 : cfg.perSlotChance;
  }
}
function wildCapFor(inWin: boolean): number {
  return inWin ? RTP_CONFIG.wilds.inWins.maxPerWin : RTP_CONFIG.wilds.inNonWins.maxPerSpin;
}

// Inject visual wilds into revealed trio (scenario-aware)
function maybeInjectWilds(base: [number,number,number], scen: ScenarioName): [number,number,number] {
  const inWin = scen !== "none";
  const rate = wildRateFor(scen, inWin);
  const cap  = wildCapFor(inWin);
  const out  = base.slice() as [number,number,number];
  let made = 0;
  for (let i=0;i<3;i++){
    if (made >= cap) break;
    if (Math.random() < rate) {
      out[i] = isSuper(base[i]) ? WILD_SUPER_ID : WILD_CIVIL_ID;
      made++;
    }
  }
  return out;
}

/* ---------- Wild transformation planning ---------- */
type Plan = MatchAnalysis & {
  targets: [number, number, number];
  finalIds: [number, number, number];
  totalPayout: number;
};

function enumeratePlanCandidates(ids: [number, number, number]): Plan[] {
  const indices = [0, 1, 2] as const;
  const descriptors = ids
    .map((id) => describeConcreteCard(id))
    .filter((entry): entry is ConcreteCard => !!entry);

  const options = indices.map((i) => {
    if (!isWild(ids[i])) {
      return [ids[i]];
    }

    const isSuperWild = isWildSuper(ids[i]);
    const kind: CardKind = isSuperWild ? "super" : "civilian";

    const sameKindSource = descriptors.filter((d) => d.kind === kind);
    const pool = sameKindSource.length ? sameKindSource : descriptors;

    const combos = new Set<number>();
    for (const entry of pool) {
      combos.add(kind === "super" ? supId(entry.border, entry.color) : civId(entry.border, entry.color));
    }

    if (!combos.size) {
      for (const preset of DEFAULT_WILD_PRESETS[kind]) {
        combos.add(kind === "super" ? supId(preset.border, preset.color) : civId(preset.border, preset.color));
        if (combos.size >= 6) break;
      }
    }

    return Array.from(combos);
  });

  const plans: Plan[] = [];
  const seen = new Set<string>();

  const initialIds = [ids[0], ids[1], ids[2]] as [number, number, number];
  const initialTargets: [number, number, number] = [0, 0, 0];

  function dive(position: number, currentIds: [number, number, number], targets: [number, number, number]) {
    if (position >= indices.length) {
      const finalIds = [...currentIds] as [number, number, number];
      const key = finalIds.join(",");
      if (seen.has(key)) return;
      seen.add(key);

      const targetSnapshot = [...targets] as [number, number, number];
      const analysis = analyzeMatches(finalIds);
      plans.push({
        ...analysis,
        targets: targetSnapshot,
        finalIds,
        totalPayout: totalPayoutForMatches(analysis.matches),
      });
      return;
    }

    for (const option of options[position]) {
      const nextIds = [...currentIds] as [number, number, number];
      const nextTargets = [...targets] as [number, number, number];
      nextIds[position] = option;
      if (isWild(ids[position])) {
        nextTargets[position] = option;
      }
      dive(position + 1, nextIds, nextTargets);
    }
  }

  dive(0, initialIds, initialTargets);
  return plans;
}

type NearWinHeuristic = {
  score: number;
  bestBorderCount: number;
  bestBorderLevel: 1 | 2 | 3 | 4 | 0;
  bestColorCount: number;
  superCount: number;
  civilianCount: number;
};

function computeNearWinHeuristic(ids: [number, number, number]): NearWinHeuristic {
  const borderCounts = new Map<1 | 2 | 3 | 4, number>();
  const colorCounts = new Map<number, number>();
  let superCount = 0;
  let civilianCount = 0;

  for (const id of ids) {
    const border = borderOf(id);
    if (isNonZeroBorder(border)) {
      borderCounts.set(border, (borderCounts.get(border) ?? 0) + 1);
    }
    const color = colorOf(id);
    if (color >= 0) {
      colorCounts.set(color, (colorCounts.get(color) ?? 0) + 1);
    }
    if (isSuper(id)) {
      superCount++;
    } else if (!isWild(id)) {
      civilianCount++;
    }
  }

  let bestBorderCount = 0;
  let bestBorderLevel: 1 | 2 | 3 | 4 | 0 = 0;
  for (const [border, count] of borderCounts) {
    if (count > bestBorderCount || (count === bestBorderCount && border > bestBorderLevel)) {
      bestBorderCount = count;
      bestBorderLevel = border;
    }
  }

  let bestColorCount = 0;
  for (const [, count] of colorCounts) {
    if (count > bestColorCount) {
      bestColorCount = count;
    }
  }

  const borderWeight = payoutForScenario("sameBorder") || 1;
  const colorWeight = payoutForScenario("sameCharacter") || 1;
  const superWeight = payoutForScenario("allSuper") || 1;

  const superScore = superCount === ids.length ? superWeight : (superCount * superWeight) / Math.max(ids.length, 1);

  const score =
    bestBorderCount * borderWeight +
    bestColorCount * colorWeight +
    superScore +
    (bestBorderLevel ? bestBorderLevel * 0.05 : 0) +
    Math.max(superCount, civilianCount) * 0.01;

  return {
    score,
    bestBorderCount,
    bestBorderLevel,
    bestColorCount,
    superCount,
    civilianCount,
  };
}

function firstScenarioPriorityIndex(matches: WinScenario[]): number {
  if (!matches.length) return SCENARIO_PRIORITY.length;
  return SCENARIO_PRIORITY.indexOf(matches[0]);
}

function countPlanChanges(plan: Plan): number {
  return plan.targets.filter((value) => value !== 0).length;
}

function selectPlan(plans: Plan[], comparator: (a: Plan, b: Plan) => number): Plan {
  return plans.reduce((best, candidate) => (comparator(candidate, best) > 0 ? candidate : best));
}

function comparePlansForBest(a: Plan, b: Plan): number {
  if (a.totalPayout !== b.totalPayout) {
    return a.totalPayout > b.totalPayout ? 1 : -1;
  }
  if (a.matches.length !== b.matches.length) {
    return a.matches.length > b.matches.length ? 1 : -1;
  }
  const changesA = countPlanChanges(a);
  const changesB = countPlanChanges(b);
  if (changesA !== changesB) {
    return changesA < changesB ? 1 : -1;
  }

  if (!a.matches.length && !b.matches.length) {
    const heuristicA = computeNearWinHeuristic(a.finalIds);
    const heuristicB = computeNearWinHeuristic(b.finalIds);
    if (heuristicA.score !== heuristicB.score) {
      return heuristicA.score > heuristicB.score ? 1 : -1;
    }
    if (heuristicA.bestBorderCount !== heuristicB.bestBorderCount) {
      return heuristicA.bestBorderCount > heuristicB.bestBorderCount ? 1 : -1;
    }
    if (heuristicA.bestBorderLevel !== heuristicB.bestBorderLevel) {
      return heuristicA.bestBorderLevel > heuristicB.bestBorderLevel ? 1 : -1;
    }
    if (heuristicA.bestColorCount !== heuristicB.bestColorCount) {
      return heuristicA.bestColorCount > heuristicB.bestColorCount ? 1 : -1;
    }
    if (heuristicA.superCount !== heuristicB.superCount) {
      return heuristicA.superCount > heuristicB.superCount ? 1 : -1;
    }
  }

  const priorityA = firstScenarioPriorityIndex(a.matches);
  const priorityB = firstScenarioPriorityIndex(b.matches);
  if (priorityA !== priorityB) {
    return priorityA < priorityB ? 1 : -1;
  }
  const keyA = a.finalIds.join(",");
  const keyB = b.finalIds.join(",");
  if (keyA === keyB) return 0;
  return keyA < keyB ? 1 : -1;
}

function comparePlansForNoWin(a: Plan, b: Plan): number {
  const aWins = a.matches.length > 0;
  const bWins = b.matches.length > 0;
  if (aWins !== bWins) {
    return aWins ? -1 : 1;
  }
  if (a.totalPayout !== b.totalPayout) {
    return a.totalPayout < b.totalPayout ? 1 : -1;
  }
  const changesA = countPlanChanges(a);
  const changesB = countPlanChanges(b);
  if (changesA !== changesB) {
    return changesA < changesB ? 1 : -1;
  }

  if (!a.matches.length && !b.matches.length) {
    const heuristicA = computeNearWinHeuristic(a.finalIds);
    const heuristicB = computeNearWinHeuristic(b.finalIds);
    if (heuristicA.score !== heuristicB.score) {
      return heuristicA.score > heuristicB.score ? 1 : -1;
    }
    if (heuristicA.bestBorderCount !== heuristicB.bestBorderCount) {
      return heuristicA.bestBorderCount > heuristicB.bestBorderCount ? 1 : -1;
    }
    if (heuristicA.bestBorderLevel !== heuristicB.bestBorderLevel) {
      return heuristicA.bestBorderLevel > heuristicB.bestBorderLevel ? 1 : -1;
    }
    if (heuristicA.bestColorCount !== heuristicB.bestColorCount) {
      return heuristicA.bestColorCount > heuristicB.bestColorCount ? 1 : -1;
    }
    if (heuristicA.superCount !== heuristicB.superCount) {
      return heuristicA.superCount > heuristicB.superCount ? 1 : -1;
    }
  }

  const keyA = a.finalIds.join(",");
  const keyB = b.finalIds.join(",");
  if (keyA === keyB) return 0;
  return keyA < keyB ? 1 : -1;
}

function planNoWin(ids: [number,number,number]): Plan {
  const candidates = enumeratePlanCandidates(ids);
  if (!candidates.length) {
    const finalIds = [ids[0], ids[1], ids[2]] as [number, number, number];
    const analysis = analyzeMatches(finalIds);
    return {
      ...analysis,
      targets: [0, 0, 0],
      finalIds,
      totalPayout: totalPayoutForMatches(analysis.matches),
    };
  }
  return selectPlan(candidates, comparePlansForNoWin);
}

function planBest(ids: [number,number,number], prefer: RTPConfig["transform"]["strategy"]): Plan {
  const candidates = enumeratePlanCandidates(ids);
  if (!candidates.length) {
    return planNoWin(ids);
  }

  let pool = candidates;
  if (prefer === "preferSameBorder") {
    const subset = candidates.filter((plan) => plan.matches.includes("sameBorder"));
    if (subset.length) pool = subset;
  } else if (prefer === "preferSameCharacter") {
    const subset = candidates.filter((plan) => plan.matches.includes("sameCharacter"));
    if (subset.length) pool = subset;
  }

  return selectPlan(pool, comparePlansForBest);
}

/* ---------
---------------------------------
   Forced generators for each scenario
   (We ensure scenarios are mutually exclusive)
-------------------------------------------*/

// Forced base generators (no wilds inside here)
function forceSameBorder(): [number,number,number] {
  const b = ri(1,4) as 1|2|3|4;
  const colors = pickN([0,1,2,3,4], 3);
  const ids = [ superRow(b)[colors[0]], civilianRow(b)[colors[1]], civilianRow(b)[colors[2]] ] as [number,number,number];
  return shuffle(ids) as [number,number,number];
}
function forceSameCharacter(): [number,number,number] {
  const c = ri(0,4);
  const civPool = [1,2,3,4].map(b => civilianRow(b as 1|2|3|4)[c]);
  const supPool = [1,2,3,4].map(b => superRow(b as 1|2|3|4)[c]);
  const first = pickN(civPool, 1);
  const rest = civPool.concat(supPool).filter(x => x !== first[0]);
  return shuffle([first[0], ...pickN(rest,2)]) as [number,number,number];
}
function forceAllSuper(): [number,number,number] {
  const borders = pickN([1,2,3,4], 3) as (1|2|3|4)[];
  const colors  = pickN([0,1,2,3,4], 3);
  return [ supId(borders[0],colors[0]), supId(borders[1],colors[1]), supId(borders[2],colors[2]) ];
}


/* ------------------------------------------
   Public API: mock round
   Returns [id1, id2, id3, winAmount, wasNearToWin, ScenarioResult[]]  (ids are the *initial* revealed ones)
   - Client will second-flip any wild using `lastTransformTargets`
-------------------------------------------*/
let forceNext: ScenarioName | null = null;
let manualOverride: ManualRoundOverride | null = null;

export function forceNextOutcome(s: ScenarioName | null) {
  if (s !== null) {
    manualOverride = null;
  }
  forceNext = s;
}

export function setManualRoundOverride(override: ManualRoundOverride | null) {
  manualOverride = override;
  if (override) {
    forceNext = null;
  }
}

export function getManualRoundOverride(): ManualRoundOverride | null {
  return manualOverride;
}

let consecutiveWins = 0;

export let lastNearToWin = false;
export type NearToWinKind = "sameBorder" | "sameCharacter" | "allSuper" | "none";
export let lastNearToWinKind: NearToWinKind = "none";
export let lastWinScenarios: ScenarioResult[] = [];






/* ---------------- Public API: mock round ---------------- */
export async function getRoundMock(): Promise<RoundTuple> {
  await wait(250);

  if (manualOverride) {
    const current = manualOverride;
    if (!current.persist) {
      manualOverride = null;
    }
    const reveal = current.cards.map((card, idx) => symbolPlanToId(card, idx)) as [number, number, number];
    const manualTargets = current.cards.map((card, idx) => transformPlanToId(card, idx, reveal[idx])) as [number, number, number];

    const targets: [number, number, number] = [0, 0, 0];
    const finalIds = [...reveal] as [number, number, number];

    const wildSlots = reveal.map((id) => isWild(id));
    const manualTransformApplied = manualTargets.some((value) => value !== 0);

    if (manualTransformApplied) {
      for (let i = 0; i < manualTargets.length; i++) {
        const target = manualTargets[i];
        if (!target) continue;
        targets[i] = target;
        finalIds[i] = target;
      }
    } else if (wildSlots.some(Boolean)) {
      const plan = planBest(reveal, current.transformStrategy ?? RTP_CONFIG.transform.strategy);
      for (let i = 0; i < plan.targets.length; i++) {
        const target = plan.targets[i];
        if (!wildSlots[i] || !target) continue;
        targets[i] = target;
        finalIds[i] = target;
      }
    }

    lastTransformTargets = targets;

    const bet = betSizes[betArea.currentBetIndex] ?? 1;
    let scenarioResults = computeScenarioResults(finalIds, bet);
    let win = scenarioResults.reduce((sum, entry) => sum + entry.amount, 0);

    if (RTP_CONFIG.guards.maxConsecutiveWins && scenarioResults.length) {
      if (consecutiveWins >= RTP_CONFIG.guards.maxConsecutiveWins) {
        scenarioResults = [];
        win = 0;
      }
    }

    consecutiveWins = scenarioResults.length ? consecutiveWins + 1 : 0;
    lastWinScenarios = scenarioResults;
    lastNearToWinKind = nearToWinKindTwo(finalIds[0], finalIds[1]);
    lastNearToWin = lastNearToWinKind !== "none";

    return [reveal[0], reveal[1], reveal[2], win, lastNearToWin, scenarioResults];
  }

  // 1) choose base scenario
  let scen: ScenarioName = (forceNext ?? pickScenario());
  forceNext = null;

  // 2) build base trio
  let base: [number,number,number];
  switch (scen) {
    case "sameBorder":    base = forceSameBorder();    break;
    case "sameCharacter": base = forceSameCharacter(); break;
    case "allSuper":      base = forceAllSuper();      break; 

    // case "nearTowin": base = true
    default: { // "none"
 
      const pool = Array.from({ length: 42 }, (_, i) => i + 1);
      base = pickN(pool, 3) as [number,number,number];
      if (RTP_CONFIG.guards.avoidAccidentalWinsInNone) {
        for (let t=0; t<2; t++) {
          const ev = evaluateScenario(base as any);
          if (ev === "none") break;
          base = pickN(pool, 3) as [number,number,number];
        }
      }
      break;
    }
  }

  // 3) visual wilds (scenario-aware, with caps)
  const revealed = maybeInjectWilds(base, scen);

lastNearToWinKind = nearToWinKindTwo(revealed[0], revealed[1]);
lastNearToWin = (lastNearToWinKind !== "none");  

  // 4) Plan transforms
  let plan;
  if (scen === "none" && !RTP_CONFIG.transform.allowUpgradeFromNone) {
    plan = planNoWin(revealed);
  } else {
    plan = planBest(revealed, RTP_CONFIG.transform.strategy);
  }
  lastTransformTargets = plan.targets;

  const bet = betSizes[betArea.currentBetIndex] ?? 1;
  let scenarioResults = computeScenarioResults(plan.finalIds, bet);
  let win = scenarioResults.reduce((sum, entry) => sum + entry.amount, 0);

  if (RTP_CONFIG.guards.maxConsecutiveWins && scenarioResults.length) {
    if (consecutiveWins >= RTP_CONFIG.guards.maxConsecutiveWins) {
      scenarioResults = [];
      win = 0;
    }
  }

  consecutiveWins = scenarioResults.length ? (consecutiveWins + 1) : 0;
  lastWinScenarios = scenarioResults;

  return [revealed[0], revealed[1], revealed[2], win, lastNearToWin, scenarioResults];
}
// === add near-to-win exports near your other hints ===
function nearToWinKindTwo(a: number, b: number): NearToWinKind {
  // SAME CHARACTER: if any is wild → always possible; otherwise colors must match
  const sameCharacterPossible =
    isWild(a) || isWild(b) ? true : (colorOf(a) !== -1 && colorOf(a) === colorOf(b));

  // SAME BORDER: if any is wild → always possible; otherwise borders must match
  const sameBorderPossible =
    isWild(a) || isWild(b) ? true : (borderOf(a) !== 0 && borderOf(a) === borderOf(b));

  // ALL SUPER: both must be SUPER-compatible (super or super-wild)
  const allSuperPossible =
    (isSuper(a) || isWildSuper(a)) && (isSuper(b) || isWildSuper(b));

  // pick a “best” tease kind by your payout priority
  if (sameCharacterPossible) return "sameCharacter";
  if (sameBorderPossible)    return "sameBorder";
  if (allSuperPossible)      return "allSuper";
  return "none";
}



// BONUS RESPONSES
 
// ----- Types -----
type BonusCell = number | "power-up";

interface Range {
  min: number;
  max: number;
  step?: number; // optional: e.g., 0.5 steps
}

type CashSource = number[] | Range;

interface RowRule {
  count: number;            // how many boxes in this row
  includePowerUp: boolean;  // should this row contain exactly one "power-up"?
  cash: CashSource;         // how to generate cash values for the rest
  uniqueCash?: boolean;     // if true and 'cash' is array, avoid duplicates when possible
}

// ----- Row layout (bottom→top) -----
const ROWS_ORDER = [5, 3, 2, 1]; // matches your Blocks: [wood, brick, stone, gold]

// ----- Payout rules per row (index 0 = bottom row) -----
const ROW_RULES: RowRule[] = [
  // Row 0 (5 cells): small values + 1 power-up
  { count: 7, includePowerUp: true, cash: [0.5, 0.7, 0.95, 0.25, 0.35, 0.75, 0.85,0.4, 0.15], uniqueCash: false },

  { count: 5, includePowerUp: true, cash: [1, 3, 4, 5, 2, 1.5, 2.5], uniqueCash: false },

  // Row 1 (3 cells): mid values + 1 power-up
  { count: 3, includePowerUp: true, cash: [10, 20, 30, 40, 50], uniqueCash: false },

  // Row 2 (2 cells): higher values + 1 power-up
  { count: 2, includePowerUp: true, cash: [50, 75, 100, 150], uniqueCash: false },

  // Row 3 (1 cell): top row (final) — cash only, no power-up
  { count: 1, includePowerUp: false, cash: [500, 1000, 2000], uniqueCash: false },
];

// ----- Helpers -----
const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const pickFromArray = <T>(arr: T[], avoid?: Set<T>): T => {
  if (!avoid || avoid.size === 0) return arr[randInt(0, arr.length - 1)];
  const options = arr.filter(v => !avoid.has(v));
  return (options.length ? options : arr)[randInt(0, (options.length ? options : arr).length - 1)];
};

const pickFromRange = (r: Range): number => {
  const step = r.step ?? 1;
  const steps = Math.round((r.max - r.min) / step);
  return +(r.min + randInt(0, steps) * step).toFixed(step % 1 ? 2 : 0);
};

const nextCash = (source: CashSource, used?: Set<number>, unique = false): number => {
  if (Array.isArray(source)) {
    return unique ? pickFromArray(source, used as Set<number>) : pickFromArray(source);
  }
  return pickFromRange(source);
};

// ----- Main: generate one row for a given rowIndex -----
export function generateBonusRow(rowIndex: number): BonusCell[] {
  const rule = ROW_RULES[rowIndex];
  if (!rule) throw new Error(`No rule for row ${rowIndex}`);

  const res: BonusCell[] = new Array(rule.count);
  const used = new Set<number>();

  // Place power-up if this row requires it
  let powerUpIdx = -1;
  if (rule.includePowerUp) {
    powerUpIdx = randInt(0, rule.count - 1);
    res[powerUpIdx] = "power-up";
    console.log(powerUpIdx,"powerUpIdx")
  }

  // Fill the rest with cash
  for (let i = 0; i < rule.count; i++) {
    if (i === powerUpIdx) continue;
    const val = nextCash(rule.cash, used, !!rule.uniqueCash);
    if (rule.uniqueCash && Array.isArray(rule.cash)) used.add(val);
    res[i] = val;
  }

  return res;
}





// export let bonusData = [["power-up",1,4,3,5],[30,"power-up",50],["power-up",100],[1000]];
